import { createContext } from 'svelte'
import type { ApiClient } from '@tadpoles/api-client'
import type { Child, FamilyClassroom, Guardian } from '@tadpoles/common'

import { createUserContext, type UserContext } from '../context/user-context.js'
import { ClassroomService } from './classroom.svelte.js'
import { ChildService } from './child.svelte.js'
import { AttachmentService } from './attachment.svelte.js'
import { FeatureService } from './feature.svelte.js'
import { GuardianService } from './guardian.svelte.js'
import { LocalEncryptionService } from './local-encryption.svelte.js'
import { MessagingService } from './messaging/service.svelte.js'
import { NotificationSettingsService } from './notification-settings.js'
import type { Connectivity } from './connectivity/service.js'
import type { FamilyUserLocalBacking } from './persistence/family-local-backing.js'
import type { FeatureAssignment } from './persistence/stores/feature-store.js'
import type { PushNotificationsService } from './push-notifications/service.js'
import { OrderedServiceLifecycle, type ServiceLifecycle } from './service-lifecycle.js'
import type { UserScopedServiceGraph, UserScopedServiceScope } from './user-scoped-service-scope.js'

export type UserScopedDomainData = {
    classrooms: FamilyClassroom[]
    children: Child[]
    features: FeatureAssignment[]
    guardians: Guardian[]
}

export type UserScopedServicesOptions = {
    apiClient: ApiClient
    connectivity?: Connectivity
    pushNotifications?: Pick<PushNotificationsService, 'subscribe'>
    restoreDomainData?: boolean
}

export type UserScopedProfileUpdate = {
    displayName: string
    language?: string
    mobileNumber?: string
}

export class UserScopedServices
    implements ServiceLifecycle, UserScopedServiceGraph, UserScopedServiceScope
{
    public readonly attachment: AttachmentService
    public readonly classroom: ClassroomService
    public readonly child: ChildService
    public readonly feature: FeatureService
    public readonly guardian: GuardianService
    public readonly localEncryption: LocalEncryptionService
    public readonly messaging: MessagingService
    public readonly notificationSettings: NotificationSettingsService
    readonly #lifecycle: OrderedServiceLifecycle
    #context = $state.raw<UserContext>()
    #resolutionPhase: 'composing' | 'composed' | 'destroying' | 'destroyed' = 'composing'

    public constructor(
        context: UserContext,
        localBacking: FamilyUserLocalBacking,
        options: UserScopedServicesOptions
    ) {
        const restoreDomainData = options.restoreDomainData ?? true
        this.#context = context
        this.attachment = new AttachmentService(this, options.apiClient.attachments, localBacking)
        this.classroom = new ClassroomService(this, localBacking, restoreDomainData)
        this.child = new ChildService(this, localBacking, restoreDomainData)
        this.guardian = new GuardianService(this, localBacking, restoreDomainData)
        this.feature = new FeatureService(this, localBacking, restoreDomainData)
        this.localEncryption = new LocalEncryptionService(this, localBacking)
        this.messaging = new MessagingService(this, localBacking, options.apiClient, {
            connectivity: options.connectivity,
            pushNotifications: options.pushNotifications
        })
        this.notificationSettings = new NotificationSettingsService(this, options.apiClient)
        this.#lifecycle = new OrderedServiceLifecycle([
            this.attachment,
            this.classroom,
            this.child,
            this.guardian,
            this.feature,
            this.localEncryption,
            this.messaging
        ])
        this.#resolutionPhase = 'composed'
    }

    public get context(): UserContext {
        const context = this.#context
        if (context === undefined) {
            throw new Error('User-scoped services require a user context.')
        }

        return context
    }

    public initialize = async (): Promise<void> => {
        await this.#lifecycle.initialize()
    }

    public resume = async (): Promise<void> => {
        await this.#lifecycle.resume()
    }

    public suspend = async (): Promise<void> => {
        await this.#lifecycle.suspend()
    }

    public destroy = async (): Promise<void> => {
        if (this.#resolutionPhase === 'destroying' || this.#resolutionPhase === 'destroyed') {
            return
        }

        this.#resolutionPhase = 'destroying'

        try {
            this.notificationSettings.destroy()
            await this.#lifecycle.destroy()
        } finally {
            this.#resolutionPhase = 'destroyed'
        }
    }

    public resolveServices(): UserScopedServiceGraph {
        if (this.#resolutionPhase === 'composing') {
            throw new Error('User-scoped services cannot be resolved during graph composition.')
        }

        if (this.#resolutionPhase === 'destroyed') {
            throw new Error('User-scoped services cannot be resolved after graph destruction.')
        }

        return this
    }

    public replaceContext(context: UserContext): void {
        if (context.person.key !== this.context.person.key) {
            throw new Error('Cannot replace a user context with a different person identity.')
        }

        this.#context = context
    }

    public hydrate(data: UserScopedDomainData): void {
        this.classroom.hydrate(data.classrooms)
        this.child.hydrate(data.children)
        this.guardian.hydrate(data.guardians)
        this.feature.hydrate(data.features)
        this.messaging.reconcileEligibility()
    }

    public replaceAll(data: UserScopedDomainData): void {
        this.#startCacheWrite('classrooms', this.classroom.replaceAll(data.classrooms))
        this.#startCacheWrite('children', this.child.replaceAll(data.children))
        this.#startCacheWrite('guardians', this.guardian.replaceAll(data.guardians))
        this.#startCacheWrite('features', this.feature.replaceAll(data.features))
        this.messaging.reconcileEligibility()
        this.#startBackgroundOperation(
            'refresh messaging after user entry',
            this.messaging.refreshMetadata()
        )
    }

    public applyProfileUpdate(update: UserScopedProfileUpdate): void {
        this.replaceContext(
            createUserContext({
                ...this.context.person,
                displayName: update.displayName,
                mobileNumber: update.mobileNumber || undefined,
                ...(update.language ? { language: update.language } : {})
            })
        )
    }

    public applyLanguageUpdate(language: string): void {
        this.replaceContext(
            createUserContext({
                ...this.context.person,
                language
            })
        )
    }

    #startCacheWrite(domain: string, write: Promise<void>): void {
        void write.catch((error) => {
            if (!error || !(error instanceof DOMException) || error.name !== 'AbortError') {
                console.warn(`Unable to persist user-scoped ${domain}.`, error)
            }
        })
    }

    #startBackgroundOperation(description: string, operation: Promise<void>): void {
        void operation.catch((error) => {
            if (!error || !(error instanceof DOMException) || error.name !== 'AbortError') {
                console.warn(`Unable to ${description}.`, error)
            }
        })
    }
}

export const [getUserScopedServices, setUserScopedServices] = createContext<UserScopedServices>()
